/**
 * NotificationCenter Component
 *
 * Hover menu for notifications: filters, list, and bulk actions.
 */

import { Trash2, CheckCheck } from 'lucide-react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { useNotificationContext } from '@/context/NotificationContext';
import { NotificationBadge } from './NotificationBadge';
import { NotificationFilters } from './NotificationFilters';
import { NotificationList } from './NotificationList';

export function NotificationCenter() {
  const {
    isOpen,
    setIsOpen,
    unreadCount,
    notifications,
    markAllAsRead,
    dismissAll,
    filter,
    systemNotificationCount,
    clearSystemNotifications,
  } = useNotificationContext();

  const hasNotifications = notifications.length > 0;
  const hasUnread = unreadCount > 0;
  const isSystemFilter = filter === 'system';
  const hasSystemNotifications = systemNotificationCount > 0;

  // Show footer when there's content to act on
  const showFooter = isSystemFilter ? hasSystemNotifications : hasNotifications;

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const handleDismissAll = async () => {
    await dismissAll();
  };

  const handleClearSystem = () => {
    clearSystemNotifications();
  };

  return (
    <HoverCard open={isOpen} onOpenChange={setIsOpen} openDelay={0} closeDelay={100}>
      <HoverCardTrigger asChild>
        <NotificationBadge data-onboarding-id="notifications" />
      </HoverCardTrigger>

      <HoverCardContent align="end" className="w-80 p-1 bg-background">
        <div className="mb-1 px-2 py-1 text-xs text-popover-foreground dark:text-card-foreground">Notifications</div>
        <NotificationFilters />
        <div className="my-1 max-h-[350px] overflow-y-auto">
          <NotificationList />
        </div>
        {showFooter && (
          isSystemFilter ? (
            <ButtonGroup orientation="vertical" className="w-full">
              <Button variant="outline" size="sm" className="w-full justify-start" onClick={handleClearSystem}>
                <Trash2 className="h-4 w-4" />
                Clear {systemNotificationCount}
              </Button>
            </ButtonGroup>
          ) : (
            <ButtonGroup orientation="vertical" className="w-full">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={handleMarkAllRead}
                disabled={!hasUnread}
              >
                <CheckCheck className="h-4 w-4" />
                Mark all read
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start" onClick={handleDismissAll}>
                <Trash2 className="h-4 w-4" />
                Clear all
              </Button>
            </ButtonGroup>
          )
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
